import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Grid, Stack,Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ProgressChartWidget from 'components/Widgets/ProgressChart';
import HighlightedText from "components/GlobalSearch/HighlightText";
import MultiLineWidget from '../../Widgets/MultiLineChart';
import KpiWithPie from '../common/KpiWithPie';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import RowTypography from 'components/Table/RowTypography';
import LoaderComponent from 'components/Loader';
import {
  fetchTableauReportUsage,
  fetchTableauTotalReports,
  fetchReportsTableauTableData,
  fetchTableauReportsFilterOptions,  // Updated import
  fetchTableauTopPublisherDetails,
  fetchTableauReportsTrendData
} from '../../../service/service';
import { getPepwiseSectorList } from 'service/service';
import moment from 'moment';

type TableauReportsProps = {
  filters?: {
    sector?: string | string[];
    filterOption?: string;
    startDate?: string;
    endDate?: string;
    filterList?: null | { [key: string]: any };
    chartTimePeriod?: string;
  };
  timeRange?: string;
  selectedSectors?: string[];
  checkedListRef?: React.MutableRefObject<any>;
  resetFiltersRef?: React.MutableRefObject<(() => void) | null>;
};

interface TotalReportsData {
  totalReports: number;
  totalActiveReports: number;
  totalNewReports: number;
  totalModifiedReports: number;
  sectorBreakdown: Array<{
    sector: string;
    totalReports: number;
    activeReports: number;
  }>;
  filtersApplied: {
    sector: string;
  };
}

interface ReportUsageData {
  totalReportUsage: number;
  sectorBreakdown: Array<{ sector: string; reportUsage: number }>;
  filtersApplied: {
    sector: string;
  };
}
interface TopPublisherData {
  totalPublisher: number;
  publisherDetail: Array<{ publisher: string; sector:string; distinctActivities: number;
              activityCount: number;
              views: number; workbooks: number;
              datasources: number; dataflows:number }>;
  filtersApplied: {
    sector: string;
  },
  artifactData: Array<string>,
}


const transformPublisherData = (data: any[]) => {
    return data
      .map((item) => ({
        name: item.publisher,
        sector:item.sector,
        value: item.distinctActivities,
        valueInBytes: item.distinctActivities,
        unit: "count",
        item
      }));
};


const transformPublisherSeriesData = (data:any[], artifactNames:any[], colors:any) => {
  const barColor = colors.widget?.colors?.color1;

  const artifactSeriesData = artifactNames.map((artifact, index) => ({
      name: artifact.toUpperCase(),
      type: 'bar',
      stack: 'total',
      showBackground: false,
      itemStyle: {
        color: barColor
      },
      label: {
        show: false,
        position: 'right',
        color: colors.widget?.chart?.labelColorSecondary,
        clip: false,
        formatter: (params: any) => {
          const publisherIdx = data.length - 1 - params.dataIndex;
          const totalActivities = data[publisherIdx].distinctActivities;

          return `${totalActivities}`;
        }
      },
      data: data.map((item: any, rowIdx: number) => {
        const rawEnv = Number(item[artifact] || 0);
        return rawEnv;
      }).reverse()
    }));
    return artifactSeriesData

}

interface Report {
  id: string;
  reportName: string;
  projectName: string;
  projectOwner: string;
  sector: string;
  lastAccessedDate: string;
  createdDate: string;
  modifiedDate: string;
  usageCount: number;
  viewId: string;
  workbookId: string;
  workbookName?: string;
  userName: string;
  userEmail: string;
  userGpid?: string;
  instance: string;
  state?: string;
  status: 'active' | 'inactive' | 'new' | 'modified';
}

const getLicenseStatusColors = (colors:any) => ({
  'active': { color: colors.white.main, bg: colors.boxColor?.blue?.color },
  'inactive': { color: colors.white.main, bg: colors.boxColor?.red?.bgColor },
  'new': { color: colors.white.main, bg: colors.boxColor?.orange?.bgColor },
  'modified': { color: colors.white.main, bg: colors.boxColor?.orange?.bgColor }
});

// Formatter functions for table columns
const reportNameFormatter = ({ row }: any) => <RowTypography>{row.reportName || ''}</RowTypography>;
const projectFormatter = ({ row }: any) => <RowTypography>{row.projectName || ''}</RowTypography>;
const projectOwnerFormatter = ({ row }: any) => <RowTypography>{row.projectOwner || ''}</RowTypography>;
const sectorFormatter = ({ row }: any) => <RowTypography>{row.sector || ''}</RowTypography>;
const lastAccessedFormatter = ({ row }: any) => <RowTypography>{row.lastAccessedDate || ''}</RowTypography>;
const createdDateFormatter = ({ row }: any) => <RowTypography>{row.createdDate || ''}</RowTypography>;
const modifiedDateFormatter = ({ row }: any) => <RowTypography>{row.modifiedDate || ''}</RowTypography>;
const usageCountFormatter = ({ row }: any) => <RowTypography>{row.usageCount || 0}</RowTypography>;
const statusFormatter = ({ row }: { row: Report }, colors:any) => {
  const licenseStatusColors = getLicenseStatusColors(colors);
  const config = licenseStatusColors[row.status] || licenseStatusColors['active'];
  return (
    <Chip
      label={row.status?.toUpperCase() || ""}
      size="medium"
      sx={{
        backgroundColor: `${config.bg} !important`,
        color: `${config.color} !important`,
        fontSize: '12px',
        borderRadius: '4px',
        height: '28px',
        minWidth: '116px',
        justifyContent: 'center',
        '& .MuiChip-label': {
          p: '4px 8px',
          fontWeight: 500,
          color: `${config.color} !important`,
          overflow: 'visible',
          textOverflow: 'clip',
          whiteSpace: 'nowrap'
        }
      }}
    />
  );
};

const TableauReports = ({
  timeRange,
  selectedSectors,
  checkedListRef,
  filters: externalFilters,
  resetFiltersRef
}: TableauReportsProps) => {
  const theme = useTheme();
  const colors = theme.palette;
  const [totalReportsData, setTotalReportsData] = useState<TotalReportsData>({
    totalReports: 0,
    totalActiveReports: 0,
    totalNewReports: 0,
    totalModifiedReports: 0,
    sectorBreakdown: [],
    filtersApplied: {
      sector: 'all'
    }
  });
  const [reportUsageData, setReportUsageData] = useState<ReportUsageData>({
    totalReportUsage: 0,
    sectorBreakdown: [],
    filtersApplied: {
      sector: 'all'
    }
  });

const [topPublisherData, setTopPublisherData] = useState<TopPublisherData>({
    totalPublisher: 0,
    publisherDetail: [],
    filtersApplied: {
      sector: 'all'
    },
    artifactData:[]
  });

  const [isTotalReportsLoading, setIsTotalReportsLoading] = useState(false);
  const [isReportUsageLoading, setIsReportUsageLoading] = useState(false);
  const [allSectors, setAllSectors] = useState<string[]>([]);
  const [areSectorsLoaded, setAreSectorsLoaded] = useState(false);

  const [reportsTableData, setReportsTableData] = useState<Report[]>([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalElements: 0,
    pageSize: 100
  });

  const [tableFilters, setTableFilters] = useState({
    sector: 'all',
    searchTerm: '',
    sortBy: 'lastAccessedDate',
    orderBy: 'DESC'
  });

  const [currentFilters, setCurrentFilters] = useState<Record<string, any>>({});
  const [filterDataOptions, setFilterDataOptions] = useState<any>(null);

  const [isTopPublishersLoading, setIsTopPublishersLoading] = useState(false);
  const [trendLoading, setTrendLoading] = useState<boolean>(true);
  const [reportTrendData, setReportTrendData] = useState<any>([]);

  useEffect(() => {
    const loadSectors = async () => {
      try {
        const response = await getPepwiseSectorList();
        const sectorNames =
          response?.data?.sectors
            ?.map((sector: any) => sector?.Sector_Name)
            ?.filter((name: string) => !!name && name !== 'Unknown') || [];
        setAllSectors(sectorNames);
      } catch (error) {
        console.error('Error fetching sector list for Tableau reports:', error);
      } finally {
        setAreSectorsLoaded(true);
      }
    };

    loadSectors();
  }, []);

  const getSectorPayload = useCallback(() => {
    if (checkedListRef?.current && checkedListRef.current.length > 0) {
      return checkedListRef.current.join(',');
    }

    if (typeof externalFilters?.sector === 'string' && externalFilters.sector.toLowerCase() !== 'all') {
      return externalFilters.sector;
    }

    const sector = externalFilters?.sector;
    if (Array.isArray(sector) && sector.length > 0) {
      return sector.join(',');
    }

    return allSectors.join(',');
  }, [checkedListRef, externalFilters?.sector, allSectors]);

  const fetchTotalReports = useCallback(async () => {
    const sectorPayload = getSectorPayload();

    if (!sectorPayload) {
      return;
    }

    setIsTotalReportsLoading(true);
    try {
      const hasSpecificSelection = checkedListRef?.current && checkedListRef.current.length > 0;
      const externalSector = externalFilters?.sector;
      const hasExternalSectorFilter =
        (typeof externalSector === 'string' && externalSector.toLowerCase() !== 'all') ||
        (Array.isArray(externalSector) && externalSector.length > 0);

      let finalSectorPayload = sectorPayload;
      if (!hasSpecificSelection && !hasExternalSectorFilter) {
        finalSectorPayload = `${sectorPayload},OTHER`;
      }

      const payload = {
        sector: finalSectorPayload,
        filterOption: timeRange || externalFilters?.filterOption || 'LAST_30_DAYS'
      };

      console.log('Fetching Total Reports with payload:', payload);

      const response = await fetchTableauTotalReports(payload);

      if (response?.success && response?.data) {
        setTotalReportsData({
          totalReports: response.data.totalReports || 0,
          totalActiveReports: response.data.totalActiveReports || 0,
          totalNewReports: response.data.totalNewReports || 0,
          totalModifiedReports: response.data.totalModifiedReports || 0,
          sectorBreakdown: response.data.sectorBreakdown || [],
          filtersApplied: response.data.filtersApplied || { sector: 'all' }
        });
      } else {
        setTotalReportsData({
          totalReports: 0,
          totalActiveReports: 0,
          totalNewReports: 0,
          totalModifiedReports: 0,
          sectorBreakdown: [],
          filtersApplied: { sector: 'all' }
        });
      }
    } catch (error) {
      console.error('Error fetching total reports data:', error);
      setTotalReportsData({
        totalReports: 0,
        totalActiveReports: 0,
        totalNewReports: 0,
        totalModifiedReports: 0,
        sectorBreakdown: [],
        filtersApplied: { sector: 'all' }
      });
    } finally {
      setIsTotalReportsLoading(false);
    }
  }, [getSectorPayload, timeRange, externalFilters?.filterOption]);

  const fetchReportUsage = useCallback(async () => {
    setIsReportUsageLoading(true);
    const sectorPayload = getSectorPayload();

    if (!sectorPayload) {
      return;
    }

    try {
      const hasSpecificSelection = checkedListRef?.current && checkedListRef.current.length > 0;
      const externalSector = externalFilters?.sector;
      const hasExternalSectorFilter =
        (typeof externalSector === 'string' && externalSector.toLowerCase() !== 'all') ||
        (Array.isArray(externalSector) && externalSector.length > 0);

      let finalSectorPayload = sectorPayload;
      if (!hasSpecificSelection && !hasExternalSectorFilter) {
        finalSectorPayload = `${sectorPayload},OTHER`;
      }

      const payload = {
        sector: finalSectorPayload,
        filterOption: timeRange || externalFilters?.filterOption || 'LAST_30_DAYS'
      };

      console.log('Fetching Report Usage with payload:', payload);

      const response = await fetchTableauReportUsage(payload);

      if (response?.success && response?.data) {
        setReportUsageData({
          totalReportUsage: response.data.totalReportUsage || 0,
          sectorBreakdown: response.data.sectorBreakdown || [],
          filtersApplied: response.data.filtersApplied || { sector: 'all' }
        });
      } else {
        setReportUsageData({
          totalReportUsage: 0,
          sectorBreakdown: [],
          filtersApplied: { sector: 'all' }
        });
      }
    } catch (error) {
      console.error('Error fetching total reports data:', error);
      setReportUsageData({
        totalReportUsage: 0,
        sectorBreakdown: [],
        filtersApplied: { sector: 'all' }
      });
    } finally {
      setIsReportUsageLoading(false);
    }
  }, [getSectorPayload, timeRange, externalFilters?.filterOption, externalFilters?.sector, checkedListRef]);
  
  const fetchReportsTableData = useCallback(async (page = 1, pageSize = 100) => {
  const sectorPayload = getSectorPayload();
  
  if (!sectorPayload) {
    return;
  }

  setIsTableLoading(true);

  const hasSpecificSelection = checkedListRef?.current && checkedListRef.current.length > 0;
  const externalSector = externalFilters?.sector;
  const hasExternalSectorFilter =
    (typeof externalSector === 'string' && externalSector.toLowerCase() !== 'all') ||
    (Array.isArray(externalSector) && externalSector.length > 0);
  
    let finalSectorPayload = sectorPayload;
  if (!hasSpecificSelection && !hasExternalSectorFilter) {
    finalSectorPayload = `${sectorPayload},OTHER`;
  }

  try {
    const params = {
      page,
      pageSize,
      sector: finalSectorPayload,
      filterOption: timeRange || externalFilters?.filterOption || 'LAST_30_DAYS',
      searchTerm: tableFilters.searchTerm,
      sortBy: tableFilters.sortBy || 'reportName',
      orderBy: tableFilters.orderBy,
      filterBy: currentFilters
    };

    const response = await fetchReportsTableauTableData(params);

    if (response?.success) {
      const statusCounts = response.data?.statusCounts || {};
      const totalReportsCount = response.data?.totalReportsCount || 0;
      const activeViewCount = response.data?.activeViewCount || 0;

      console.log('Status counts received:', statusCounts);
      console.log('Total reports count:', totalReportsCount);
      console.log('Active view count:', activeViewCount);

      setReportsTableData(response.data.reports || []);
      setPagination(response.data.pagination || {
        currentPage: 1,
        totalPages: 0,
        totalElements: totalReportsCount,
        pageSize: pageSize
      });
    } else {
      setReportsTableData([]);
      setPagination({
        currentPage: 1,
        totalPages: 0,
        totalElements: 0,
        pageSize: pageSize
      });
    }
  } catch (error) {
    console.error('Error fetching reports table data:', error);
    setReportsTableData([]);
    setPagination({
      currentPage: 1,
      totalPages: 0,
      totalElements: 0,
      pageSize: pageSize
    });
  } finally {
    setIsTableLoading(false);
  }
}, [getSectorPayload, timeRange, externalFilters?.filterOption, tableFilters, currentFilters]);
  
  useEffect(() => {
    if (!areSectorsLoaded) {
      return;
    }
    fetchTotalReports();
    fetchReportUsage();
    fetchReportsTableData(1, pagination.pageSize);
    fetchTopPublisherDetails();
    fetchReportTrend();
  }, [fetchTotalReports, fetchReportUsage, fetchReportsTableData, areSectorsLoaded, selectedSectors]);
  
  useEffect(() => {
    if (areSectorsLoaded) {
      fetchReportsTableData(pagination.currentPage, pagination.pageSize);
    }
  }, [pagination.currentPage, pagination.pageSize, areSectorsLoaded]);

  useEffect(() => {
    if (areSectorsLoaded) {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      fetchReportsTableData(1, pagination.pageSize);
    }
  }, [tableFilters, currentFilters, areSectorsLoaded]);

  const fetchTopPublisherDetails = useCallback(async () => {
    setIsTopPublishersLoading(true);
    const sectorPayload = getSectorPayload();

    if (!sectorPayload) {
      return;
    }

    try {
      // Check if user has selected specific sectors or showing all
      const hasSpecificSelection = checkedListRef?.current && checkedListRef.current.length > 0;
      const externalSector = externalFilters?.sector;
      const hasExternalSectorFilter =
        (typeof externalSector === 'string' && externalSector.toLowerCase() !== 'all') ||
        (Array.isArray(externalSector) && externalSector.length > 0);

      // Add "OTHER" only when showing all sectors (no specific selection)
      let finalSectorPayload = sectorPayload;
      if (!hasSpecificSelection && !hasExternalSectorFilter) {
        finalSectorPayload = `${sectorPayload},OTHER`
      }

      const payload = {
        sector: finalSectorPayload,
        filterOption: timeRange || externalFilters?.filterOption || 'LAST_30_DAYS'
      };

      console.log('Fetching Top 10 Publisher Details with payload:', payload);

      const response = await fetchTableauTopPublisherDetails(payload);

      if (response?.success && response?.data) {
        const artifactData =  response?.data?.publisherDetail || [];
      // Extract unique artifacts names from the data
      const artifactNames = artifactData.length > 0
        ? Object.keys(artifactData[0]).filter((key) => key !== 'publisher'
                && key !== 'distinctActivities' && key !== 'activityCount'
                &&  key !== 'sector')
        : [];
        setTopPublisherData({
          totalPublisher: response.data.totalPublisher || 0,
          publisherDetail: response.data.publisherDetail || [],
          filtersApplied: response.data.filtersApplied || { sector: 'all' },
          artifactData: artifactNames
        });
      } else {
        // Reset to default if API fails
        setTopPublisherData({
          totalPublisher: 0,
          publisherDetail: [],
          filtersApplied: { sector: 'all' },
          artifactData: []
        });
      }
    } catch (error) {
      console.error('Error fetching Top Publisher data:', error);
      setTopPublisherData({
        totalPublisher: 0,
        publisherDetail: [],
        filtersApplied: { sector: 'all' },
        artifactData: []
      });
    } finally {
      setIsTopPublishersLoading(false);
    }
  }, [getSectorPayload, timeRange, externalFilters?.filterOption, externalFilters?.sector, checkedListRef]);
  
    const fetchReportTrend = useCallback(async () => {
    setTrendLoading(true);
    const sectorPayload = getSectorPayload();

    if (!sectorPayload) {
      return;
    }

    try {
      const payload = {
        sector: sectorPayload,
        filterOption: timeRange || externalFilters?.filterOption || 'LAST_30_DAYS'
      };

      console.log('Fetching Report Trends with payload:', payload);

      const response = await fetchTableauReportsTrendData(payload);
      if (response?.success && response?.data) {
        setReportTrendData(response.data.trendData);
      } else {
        console.error('Invalid Trend response structure:', response);
      }
    } catch (error) {
      console.error('Error fetching Report Trend data:', error);
      setReportTrendData([]); // Initialize as object for trend data
    } finally {
      setTrendLoading(false);
    }
  }, [getSectorPayload, timeRange, externalFilters?.filterOption, externalFilters?.sector, checkedListRef]);
  
  
  
  const formatReportCount = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  }

  const getChartOptions = useMemo(() => {
    const allDailyPoints: any[] = [];
    const xAxisLabels: string[] = [];
    const activeReportsData: number[] = [];
    const inactiveReportsData: number[] = [];
    const monthBreakpoints: { index: number; monthLabel: string }[] = [];

    const formatDate = (dateString: string, options?: Intl.DateTimeFormatOptions) => {
      const date = new Date(dateString);
      if (!Number.isNaN(date.getTime())) {
        return new Intl.DateTimeFormat('en-GB', options).format(date);
      }
      return '';
    };

    const processRawData = () => {
      reportTrendData.forEach((monthData: any) => {
        const dailyPoints = monthData.dailyPoints || [];
        if (dailyPoints.length) {
          monthBreakpoints.push({
            index: allDailyPoints.length,
            monthLabel: monthData.month
          });
          allDailyPoints.push(...dailyPoints);
        }
      });

      allDailyPoints.sort((a: any, b: any) => new Date(a.dateString).getTime() - new Date(b.dateString).getTime());
      
      allDailyPoints.forEach((point: any) => {
        xAxisLabels.push(formatDate(point.dateString, { day: 'numeric', month: 'short' }));
        activeReportsData.push(point.activeReports || 0);
        inactiveReportsData.push(point.inactiveReports || 0);
      });
    };

    processRawData();

    // Fallback if no data
    if (!allDailyPoints.length) {
      for (let i = 1; i <= 30; i++) xAxisLabels.push(`${i}`);
      activeReportsData.push(...Array(30).fill(0));
      inactiveReportsData.push(...Array(30).fill(0));
    }

    const lastDataIndex = allDailyPoints.length - 1;

    const createMarkLine = () => {
      if (lastDataIndex < 0) return undefined;
      const lastPoint = allDailyPoints[lastDataIndex];
      return {
        silent: true,
        symbol: 'none',
        lineStyle: {
          type: 'dashed',
          color: colors.widget?.chart?.legendTextColor,
          width: 1
        },
        label: {
          show: true,
          position: 'insideEndTop',
          color: colors.widget?.chart?.legendTextColor,
          fontSize: 11,
          rotate: 0,
          offset: [10, 0],
          formatter: () => `Latest by ${formatDate(lastPoint.dateString, { day: 'numeric', month: 'short', year: 'numeric' })}`
        },
        data: [{ xAxis: lastDataIndex }]
      };
    };

    const tooltipFormatter = (params: any) => {
      const dataIndex = params[0]?.dataIndex ?? 0;
      const formattedDate = allDailyPoints[dataIndex]
        ? formatDate(allDailyPoints[dataIndex].dateString, { day: 'numeric', month: 'long', year: 'numeric' })
        : 'Unknown date';

      return `
        <div style="margin-bottom: 5px; font-weight: bold;">${formattedDate}</div>
        ${params
          .map((p: any) => `
            <div style="display: flex; align-items: center; margin-bottom: 3px;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${p.color}; margin-right: 8px; border-radius: 2px;"></span>
              <span style="margin-right: 8px;">${p.seriesName}:</span>
              <span style="font-weight: bold;">${p.value.toLocaleString()}</span>
            </div>
          `).join('')}
      `;
    };

    return {
      backgroundColor: colors.widget?.background?.chart,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: colors.widget?.chart?.legendTextColor
          }
        },
        backgroundColor: colors.widget?.chart?.tooltipBg,
        borderColor: colors.widget?.chart?.legendInactiveUpDownIconColor,
        borderWidth: 1,
        textStyle: {
          color: colors.widget?.chart?.tooltipText,
          fontSize: 12
        },
        formatter: tooltipFormatter
      },
      legend: {
        data: ['Most Used', 'Unused'],
        textStyle: {
          color: colors.widget?.chart?.legendTextColor,
          fontSize: 11
        },
        itemWidth: 14,
        itemHeight: 14,
        itemGap: 15,
        icon: 'roundRect',
        bottom: '0',
        left: 'left'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: xAxisLabels,
        axisLabel: {
          color: colors.widget?.chart?.legendTextColor,
          fontSize: 11,
          interval: 0,
          rotate: 45,
          formatter: (value: string, index: number) => {
            const bp = monthBreakpoints.find(m => m.index === index);
            return bp ? bp.monthLabel : '';
          }
        },
        axisLine: {
          lineStyle: {
            color: colors.widget?.chart?.legendTextColor
          }
        },
        splitLine: {
          show: false,
          lineStyle: {
            type: 'dashed',
            color: colors.widget?.chart?.emptyCircleFill
          }
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: colors.widget?.chart?.legendTextColor,
          fontSize: 11,
          formatter: formatReportCount
        },
        axisLine: {
          lineStyle: {
            color: colors.widget?.chart?.legendTextColor
          }
        },
        splitLine: {
          show: false,
          lineStyle: {
            type: 'dashed',
            color: colors.widget?.chart?.emptyCircleFill
          }
        }
      },
      series: [
        {
          name: 'Most Used',
          type: 'line',
          data: activeReportsData,
          smooth: false,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            color: colors.boxColor?.blue?.color,
            width: 3
          },
          itemStyle: {
            color: colors.boxColor?.blue?.color
          },
          label: {
            show: false,
            position: 'top',
            color: colors.widget?.chart?.legendTextColor,
            fontSize: 10,
            formatter: (params: any) => formatReportCount(params.value)
          },
          markLine: createMarkLine()
        },
        {
          name: 'Unused',
          type: 'line',
          data: inactiveReportsData,
          smooth: false,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            color: colors.boxColor?.red?.bgColor,
            width: 3
          },
          itemStyle: {
            color: colors.boxColor?.red?.bgColor
          },
          label: {
            show: false,
            position: 'bottom',
            color: colors.widget?.chart?.legendTextColor,
            fontSize: 10,
            formatter: (params: any) => formatReportCount(params.value)
          }
        }
      ]
    };
  }, [reportTrendData, colors]);

  const sectorColorMapping: { [key: string]: string } = {
    'PBNA': '#4A90E2',
    'PFNA': '#01C008',
    'LATAM': '#FFA500',
    'AMESA': '#9B59B6',
    'AMESA/APAC': '#9B59B6',
    'EUROPE': '#c9cc19ff',
    'Europe': '#c9cc19ff',
    'CGF': '#E74C3C',
    'OTHER': '#969696ff'
  };

  const totalReportsPieData = {
    number: totalReportsData.totalReports,
    change: 12.5,
    changeType: 'increase' as const,
    pieData: [
      { name: 'Active', value: totalReportsData.totalActiveReports },
      { name: 'New', value: totalReportsData.totalNewReports },
      { name: 'Inactive', value: totalReportsData.totalReports - totalReportsData.totalActiveReports - totalReportsData.totalNewReports - totalReportsData.totalModifiedReports },
      { name: 'Modified Reports', value: totalReportsData.totalModifiedReports }
    ],
    colorMapping: {
      'Active': '#4A90E2',
      'New': '#FFA500',
      'Inactive': '#FF4444',
      'Modified Reports': '#9B59B6'
    }
  };

  const reportUsageBySectorsData = useMemo(() => {
    const pieData = reportUsageData.sectorBreakdown.length === 0
      ? [{ name: 'No Usage', value: 0 }]
      : reportUsageData.sectorBreakdown.map(item => ({
        name: item.sector,
        value: item.reportUsage
      }));

    return {
      number: reportUsageData.totalReportUsage,
      change: 12.5,
      changeType: 'increase' as const,
      pieData: pieData,
      colorMapping: {
        ...sectorColorMapping,
        'No Usage': '#95A5A6'
      }
    };
  }, [reportUsageData, sectorColorMapping]);

  type TrendDataPoint = { date: string; mostUsed: number; unused: number };

  const reportsColumns = [
  {
    key: 'reportName',
    name: 'Report Name',
    minWidth: 200,
    formatter: reportNameFormatter,
    resizable: true,
    flexGrow: 1
  },

  {
    key: 'projectName',
    name: 'Project Name',
    minWidth: 180,
    formatter: projectFormatter,
    resizable: true,
    flexGrow: 1
  },
  {
    key: 'projectOwner',
    name: 'Project Owner',
    minWidth: 150,
    formatter: projectOwnerFormatter,
    resizable: true,
    flexGrow: 1
  },
  {
    key: 'sector',
    name: 'Sector',
    minWidth: 120,
    formatter: sectorFormatter,
    resizable: true,
    flexGrow: 1
  },
  {
    key: 'status',
    name: 'Status',
    minWidth: 120,
    formatter: (props:any) => statusFormatter(props, colors),
    resizable: true,
    flexGrow: 1
  },
  {
    key: 'lastAccessedDate',
    name: 'Last Accessed Date',
    minWidth: 150,
    formatter: lastAccessedFormatter,
    resizable: true,
    flexGrow: 1
  },
  {
    key: 'createdDate',
    name: 'Created Date',
    minWidth: 120,
    formatter: createdDateFormatter,
    resizable: true,
    flexGrow: 1
  },
  {
    key: 'modifiedDate',
    name: 'Modified Date',
    minWidth: 120,
    formatter: modifiedDateFormatter,
    resizable: true,
    flexGrow: 1
  },
  {
    key: 'usageCount',
    name: 'Usage Count',
    minWidth: 120,
    formatter: usageCountFormatter,
    resizable: true,
    flexGrow: 1
  }
];

const defaultReportsColumns = [
  'Report Name',
  'Workbook Name',
  'Project Name',
  'Project Owner',
  'Sector',
  'Status',
  'State',
  'Last Accessed Date',
  'Created Date',
  'Modified Date',
  'Usage Count'
];


  const handleSorting = useCallback((sorting: any) => {
    setTableFilters(prev => ({
      ...prev,
      sortBy: sorting.sortBy,
      orderBy: sorting.sortDir
    }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, []);

  const handlePageChange = useCallback((event: any, value: any) => {
    setPagination(prev => ({ ...prev, currentPage: value }));
    fetchReportsTableData(value, pagination.pageSize);
  }, [pagination.pageSize, fetchReportsTableData]);

  const handlePageSizeChange = useCallback((event: any) => {
    const newSize = Number.parseInt(event.target.value);
    setPagination(prev => ({ ...prev, pageSize: newSize, currentPage: 1 }));
    fetchReportsTableData(1, newSize);
  }, [fetchReportsTableData]);

 const handleSearch = useCallback(async (searchDetails: any, columnKey: any) => {

  if (searchDetails?.length >= 3 && columnKey !== 'sector' && columnKey !== 'status') {
    try {
      let response;

      const fieldMapping: Record<string, string> = {
        'reportName': 'view_name',
        'projectName': 'project_name',
        'projectOwner': 'viewOwner',
        'workbookName': 'workbook_name',
        'state': 'state'
      };

      const apiField = fieldMapping[columnKey] || columnKey;

      response = await fetchTableauReportsFilterOptions(apiField, searchDetails);

      const options = response?.data || [];
      setFilterDataOptions((prev: any) => ({
        ...prev,
        [columnKey]: options
      }));
    } catch (error) {
      console.error('Error performing search:', error);
    }
  } else if (searchDetails?.length === 0 && columnKey !== 'sector' && columnKey !== 'status')  {
    const fieldMapping: Record<string, string> = {
      'reportName': 'view_name',
      'projectName': 'project_name',
      'projectOwner': 'viewOwner',
      'workbookName': 'workbook_name',
      'state': 'state'
    };

    const apiField = fieldMapping[columnKey] || columnKey;

    try {
      const response = await fetchTableauReportsFilterOptions(apiField, '');

      const initialOptions = response?.data || [];
      setFilterDataOptions((prev: any) => ({
        ...prev,
        [columnKey]: initialOptions
      }));
    } catch (error) {
      console.error('Error resetting filter options:', error);
    }
  }
}, [getSectorPayload, timeRange, externalFilters?.filterOption]);

  const handleFilterChange = useCallback((filter: { [key: string]: any }) => {
    const filterListMap: { [key: string]: any } = {};
    const clearedColumns: string[] = [];
    let hasLastAccessedDate = false;
    let hasStatus = false;

    if (filter?.lastAccessedDate?.lenght === 0) {
      filter['status'] = []
    }

    Object.keys(filter).forEach((key) => {
      const value = filter[key];

      if (value == null || (Array.isArray(value) && value.length === 0)) {
        if (key === 'lastAccessedDate' && (Array.isArray(value) && value.length === 0)) {
          clearedColumns.push('status');
        }
        clearedColumns.push(key);
        return;
      }

      if (key === 'lastAccessedDate') {
        hasLastAccessedDate = true;
      }
      if (key === 'status') {
        hasStatus = true;
      }

      if ((key === "lastAccessedDate" || key === "createdDate" || key === "modifiedDate") && Array.isArray(value) && value.length === 2) {
        const [startMoment, endMoment] = value;

        let utcStart = startMoment;
        let utcEnd = endMoment;

        if (moment.isMoment(startMoment)) {
          utcStart = moment.utc([
            startMoment.year(),
            startMoment.month(),
            startMoment.date(),
            startMoment.hours(),
            startMoment.minutes(),
            startMoment.seconds()
          ]).toISOString();
        }

        if (moment.isMoment(endMoment)) {
          utcEnd = moment.utc([
            endMoment.year(),
            endMoment.month(),
            endMoment.date(),
            endMoment.hours(),
            endMoment.minutes(),
            endMoment.seconds()
          ]).toISOString();
        }

        filterListMap[key] = [utcStart, utcEnd];
      } else if (key === 'status') {
        filterListMap[key] = value;
      } else {
        filterListMap[key] = value;
      }
    });

    if (hasLastAccessedDate && !hasStatus) {
      filterListMap.status = ['active'];
    }

    setCurrentFilters(prev => {
      const updatedFilters = {
        ...prev,
        ...filterListMap
      };
      clearedColumns.forEach((col) => {
        delete updatedFilters[col];
      });

      if (hasLastAccessedDate && hasStatus && filter?.lastAccessedDate.length === 0) {
        delete updatedFilters["status"];
      }

      return updatedFilters;
    });

    setPagination(prev => ({ ...prev, currentPage: 1 }));

    if (clearedColumns.length > 0) {
      clearedColumns.forEach(async (columnKey) => {
        try {
          const fieldMapping: Record<string, string> = {
            reportName: 'view_name',
            projectName: 'project_name',
            projectOwner: 'viewOwner',
            sector: 'viewSector',
            workbookName: 'workbook_name',
            state: 'state',
            status: 'status'
          };

          const apiField = fieldMapping[columnKey] || columnKey;

          const response = await fetchTableauReportsFilterOptions(apiField, '');
          const options = response?.data || [];

          setFilterDataOptions((prev: any) => ({
            ...prev,
            [columnKey]: options
          }));
        } catch (error) {
          console.error(
            `Error refreshing filter options for ${columnKey}:`,
            error
          );
        }
      });
    }

  }, [getSectorPayload, timeRange, externalFilters?.filterOption]);

   const handleExport = useCallback(async () => {
    try {
      const sectorPayload = getSectorPayload();
      const cleanFilters = Object.fromEntries(
        Object.entries(currentFilters).filter(([_, v]) => Array.isArray(v) ? v.length > 0 : v != null)
      );

      const hasSpecificSelection = checkedListRef?.current && checkedListRef.current.length > 0;
      const externalSector = externalFilters?.sector;
      const hasExternalSectorFilter =
        (typeof externalSector === 'string' && externalSector.toLowerCase() !== 'all') ||
        (Array.isArray(externalSector) && externalSector.length > 0);

      let finalSectorPayload = sectorPayload;
      if (!hasSpecificSelection && !hasExternalSectorFilter) {
        finalSectorPayload = `${sectorPayload},OTHER`;
      }

      const requestBody = {
        ...tableFilters,
        filterOption: timeRange || externalFilters?.filterOption || 'LAST_30_DAYS',
        sector: finalSectorPayload,
        filterBy: cleanFilters,
        exportColumns: defaultReportsColumns,
        formatType: "EXCEL",
        tableName: "tableau-reports-monitoring",
      };

      console.log('Exporting reports data with params:', requestBody);

      // ADD THIS LINE to call the export API


    } catch (error) {
      console.error('Error exporting Tableau Reports data:', error);
    }
  }, [getSectorPayload, tableFilters, currentFilters, timeRange, externalFilters?.filterOption]);
  
  const fetchFilterOptions = useCallback(async () => {
  try {
    let sectors = await getPepwiseSectorList();
    const filterSectors = sectors?.data?.sectors.map((s: any) => s.Sector_Name) || [];
    
    // Status options
    const statusOptions = ['active', 'inactive', 'new', 'modified'];
    
    const reportNameResponse = await fetchTableauReportsFilterOptions('view_name', '');
    const reportNames = reportNameResponse?.data || [];
    
    const workbookNameResponse = await fetchTableauReportsFilterOptions('workbook_name', '');
    const workbookNames = workbookNameResponse?.data || [];
    
    const projectNameResponse = await fetchTableauReportsFilterOptions('project_name', '');
    const projectNames = projectNameResponse?.data || [];
    
    const projectOwnerResponse = await fetchTableauReportsFilterOptions('viewOwner', '');
    const projectOwners = projectOwnerResponse?.data || [];
    
    const stateResponse = await fetchTableauReportsFilterOptions('state', '');
    const states = stateResponse?.data || [];
    
    setFilterDataOptions({
      sector: filterSectors,
      status: statusOptions,  // Added
      reportName: reportNames,
      workbookName: workbookNames,  // Added
      projectName: projectNames,
      projectOwner: projectOwners,
      state: states  // Added
    });
  } catch (error) {
    console.error('Error fetching filter options:', error);
  }
}, [getSectorPayload, timeRange, externalFilters?.filterOption]);

  useEffect(() => {
    if (areSectorsLoaded) {
      fetchFilterOptions();
    }
  }, [fetchFilterOptions, areSectorsLoaded]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* First Row - Total Reports, Report Usage by Sectors */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Total Reports with Pie Chart */}
        <Grid item xs={12} md={4}>
          <KpiWithPie
            isLoading={isTotalReportsLoading}
            title="Total Reports"
            hasTooltip={true}
            tooltipText="Total count of Tableau reports by New, Active, Inactive, and Modified reports"
            number={totalReportsPieData.number}
            pieData={totalReportsPieData.pieData}
            colorMapping={totalReportsPieData.colorMapping}
            containerProps={{
              sx: {
                minHeight: '350px',
              }
            }}
          />
        </Grid>

        {/* Report Usage by Sectors */}
        <Grid item xs={12} md={4}>
          <KpiWithPie
            isLoading={isReportUsageLoading}
            title="Report Usage by Sectors"
            hasTooltip={true}
            tooltipText="Total number of Tableau report views by sector"
            number={reportUsageBySectorsData.number}
            pieData={reportUsageBySectorsData.pieData}
            colorMapping={reportUsageBySectorsData.colorMapping}
            containerProps={{
              sx: {
                minHeight: '280px',
              }
            }}
          />

        </Grid>

        
        
        <Grid item xs={12} md={4}>
             <ProgressChartWidget
              title={<HighlightedText text={"Top 10 Publishers"} />}
							data={transformPublisherData(topPublisherData.publisherDetail)}
							envNames={topPublisherData.artifactData}
							seriesData={transformPublisherSeriesData(topPublisherData.publisherDetail, topPublisherData.artifactData, colors)}
              isLoading={isTopPublishersLoading}
              variant="light"
              minTitleHeight={'0px'}
              chartByCost={1}
              hasTooltip={true}
              tooltipLabel="Users who have performed highest number of publish view, workbook, datasource, and dataflow activities"
              containerProps={{
                sx: {
                  height: topPublisherData.publisherDetail.length === 0?'None': '400px',
                }
              }}
              />
        </Grid>
      </Grid>
        {/* Reports Trend */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
            <MultiLineWidget
              isLoading={trendLoading}
              title={<HighlightedText text="Reports Trend" />}
              data={getChartOptions}
              hasTooltip={true}
              tooltipLabel="Trend of most used and unused Tableau reports over the selected time period"
              containerColor={colors.widget?.background?.chart}
              titleColor={colors.widget?.text?.title}
            />
        </Grid>
      </Grid>

      {/* Reports Monitoring Table */}
      <Box mt={2}>
        {isTableLoading && reportsTableData.length === 0 ? (
          <Stack justifyContent="center" alignItems="center" minHeight="200px">
            <LoaderComponent />
          </Stack>
        ) : (
          <CustomizedDataGrid
            title="Reports Monitoring"
            tableColumns={reportsColumns}
            tableData={reportsTableData}
            tableType="tableau-reports-monitoring"
            defaultColumnNames={defaultReportsColumns}
            onSorting={handleSorting}
            currSorting={{
              sortBy: tableFilters.sortBy,
              sortDir: tableFilters.orderBy
            }}
            filterDataOptions={filterDataOptions}
            searchDataValues={{}}
            onFilterCheck={handleFilterChange}
            currFilter={currentFilters}
            onFilterSearch={handleSearch}
            onHeaderSelectAll={() => {}}
            onFilterClick={() => {}}
            showExport={true}
            fileName="tableau-reports-monitoring-report"
            tableName="tableau-reports-monitoring"
            formatType="EXCEL"
            exportColumns={defaultReportsColumns}
            exportTableData={handleExport}
            page={pagination.currentPage}
            pageSize={pagination.pageSize}
            paginationData={pagination}
            listLength={reportsTableData.length}
            handlepageSizeChange={handlePageSizeChange}
            handlePageChange={handlePageChange}
            showGoToPage={false}
            isFetching={isTableLoading}
            isDateRangeFilter={true}
            isDisabled={false}
            pageSizeOptions={[ 100, 200,500,1000]}
          />
        )}
      </Box>
    </Box>
  );
};

export default TableauReports;